import { IconProps } from '@tabler/icons-react'
import { ComponentType, SVGProps } from 'react'

import { BGColorClass, SizeOptions, TextColorClass } from '../../types/types'
import { astraTwMerge } from '../Utilities/astraTwMerge'

// Type representing all possible icon component types from ~/icons
export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>
export type AllowedIcon = ComponentType<IconProps> | IconComponent

type TablerIconWrapperProps = {
    /**
     * The Tabler icon component to render (from @tabler/icons-react) or a custom icon from ~/icons
     */
    icon: AllowedIcon
    /**
     * Size of the icon
     * @default 'medium'
     */
    size?: SizeOptions
    /**
     * Tailwind text color class
     */
    color?: TextColorClass
    /**
     * Animation to apply to the icon
     */
    animate?: 'spin'
    /**
     * Background color class
     */
    backgroundColor?: BGColorClass
    /**
     * Background shape
     */
    backgroundShape?: 'rounded' | 'circle' | 'square'
}

const sizeMap: Record<SizeOptions, number> = {
    small: 14,
    medium: 16,
    large: 24,
}

const sizeClassMap: Record<SizeOptions, string> = {
    small: 'h-3.5 w-3.5',
    medium: 'h-4 w-4',
    large: 'h-6 w-6',
}

const backgroundShapeMap: Record<'rounded' | 'circle' | 'square', string> = {
    rounded: 'rounded-md',
    circle: 'rounded-full',
    square: 'rounded-none',
}

/**
 * Wrapper component for Tabler icons with preset stroke width and size options.
 * Also supports custom icons from ~/icons
 */
export const AstraIcon = ({ icon: IconComponent, size = 'medium', color, animate, backgroundColor, backgroundShape }: TablerIconWrapperProps) => {
    const Icon = IconComponent as ComponentType<IconProps>

    // Custom icons use className for sizing, Tabler icons use size prop
    const sizeClass = sizeClassMap[size]
    const colorClass = color ?? ''
    const animateClass = animate === 'spin' ? 'animate-spin' : ''
    const bgColorClass = backgroundColor ?? ''
    const bgShapeClass = backgroundShape ? backgroundShapeMap[backgroundShape] : ''
    const bgSizeClass = bgColorClass ? 'p-[0.15rem]' : ''
    const combinedClassName = astraTwMerge('shrink-0', sizeClass, colorClass, animateClass, bgColorClass, bgShapeClass, bgSizeClass)

    // Render with both approaches - Tabler icons will use size/stroke, custom icons will use className
    return <Icon stroke={1.5} size={sizeMap[size]} className={combinedClassName} />
}
